/**
 * Role badges — the ONE place a person's permission role gets a colour and a label.
 *
 * C34 (22/10/2026): eight places coloured role badges themselves, in three different
 * colour sets — admins were red on the dashboard and purple everywhere else, four places
 * gave a superadmin the grey employee badge, and two printed the raw word in lower case.
 * Now every page asks this file, so the Warm Hive redesign changes the colours ONCE.
 * `roleBadges.checks.mjs` fails if a page starts colouring a role by itself again.
 *
 * ⚠️ The roles are `superadmin`, `admin`, `manager`, `employee` (backend `UserRole`).
 * `director` was retired in July 2026 — don't add it back. Anything unknown is shown as an
 * employee, which is the least-privileged reading, never as an admin.
 *
 * ⚠️ This is the PERMISSION role. It is not the job title (free text) and not the reporting
 * line — see CLAUDE.md "keep these four concepts separate".
 */

export const ROLES = ['superadmin', 'admin', 'manager', 'employee'];

const LABELS = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Employee',
};

// The pill. One text shade per role that reads on the light AND the dark card (the old
// purple-400 was hard to read on white).
const BADGE = {
  superadmin: 'bg-purple-600/25 text-purple-800 dark:text-purple-200 ring-1 ring-purple-500/40',
  admin: 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  manager: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  employee: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
};

// The initials circle the org chart draws when somebody has no photo — the same colour
// family as the pill, a shade stronger because it carries no text label.
const AVATAR = {
  superadmin: 'bg-purple-600/30 text-purple-800 dark:text-purple-200',
  admin: 'bg-purple-500/25 text-purple-700 dark:text-purple-300',
  manager: 'bg-blue-500/25 text-blue-700 dark:text-blue-300',
  employee: 'bg-secondary text-secondary-foreground',
};

/** Normalise whatever the API gave us ('ADMIN', 'admin', undefined) to a known role. */
export function normRole(role) {
  const r = String(role || '').toLowerCase();
  return ROLES.includes(r) ? r : 'employee';
}

/** "Superadmin" / "Admin" / "Manager" / "Employee" — sentence case, always. */
export function roleLabel(role) {
  return LABELS[normRole(role)];
}

/** Tailwind classes for the role pill's colours (add your own padding/size). */
export function roleBadgeClass(role) {
  return BADGE[normRole(role)];
}

/** Tailwind classes for an initials circle coloured by role. */
export function roleAvatarClass(role) {
  return AVATAR[normRole(role)];
}
